import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Button, Table, Form } from 'react-bootstrap';
import { jsPDF } from 'jspdf';
import ReportHeader from './ReportHeader';
import orgmsLogo from '../images/ggsf_logo.jpeg';
import useOrgDetails from '../utils/useOrgDetails';
import useAuth from '../context/useAuth';



const DonationList = () => {
    const [donations, setDonations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState('');
    const [quarter, setQuarter] = useState('');
    const [sortOrder, setSortOrder] = useState('DESC');
    const { orgDetails, isLoading, errororg } = useOrgDetails();
    const { role } = useAuth();

    useEffect(() => {
        const fetchDonations = async () => {
            try {
                setLoading(true);
                const queryParams = new URLSearchParams();
                queryParams.append('year', year);
                if (month) queryParams.append('month', month);
                if (quarter) queryParams.append('quarter', quarter);
                if (sortOrder) queryParams.append('sortOrder', sortOrder);

                const response = await api.get(`/api/donations?${queryParams.toString()}`);
                setDonations(response.data);
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchDonations();
    }, [year, month, quarter, sortOrder]);


    const handlePrintAll = () => {
        window.print();
    };

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const quarters = [
        { value: '1', label: 'Q1 (Jan - Mar)' },
        { value: '2', label: 'Q2 (Apr - Jun)' },
        { value: '3', label: 'Q3 (Jul - Sep)' },
        { value: '4', label: 'Q4 (Oct - Dec)' }
    ];

    const getReportTitle = () => {
        if (month) {
            return `Donation Report for ${months[month - 1]} ${year}`;
        } else if (quarter) {
            const quarterLabel = quarters.find(q => q.value === quarter)?.label || '';
            return `Donation Report for ${quarterLabel} ${year}`;
        } else {
            return `Donation Report for ${year}`;
        }
    };
    // Calculate total donations safely
    const totalAmount = donations.reduce((sum, donation) => {
        const amount = parseFloat(donation.donationAmt);
        return !isNaN(amount) ? sum + amount : sum;
    }, 0);

    const summaryRightValues = [
        'Donation Report',
        `${getReportTitle()}`,
        `Report Date: ${new Intl.DateTimeFormat('en-US').format(new Date())}`
    ];
    // Convert logo to base64 (you might want to move this logic somewhere reusable)
    const convertLogoToBase64 = async (url) => {
        const response = await fetch(url);
        const blob = await response.blob();
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
        });
    };



    const handleDownloadPDF = () => {
        downloadPDF(orgDetails);  // Pass orgDetails to downloadPDF
    };

    const downloadPDF = async (orgDetails) => {
        if (donations.length === 0) return;
        if (isLoading) return <p>Loading...</p>;
        if (errororg) return <p>{errororg}</p>;
        if (!orgDetails) {
            console.error("Organization details are missing");
            return;
        }

        const logoBase64 = await convertLogoToBase64(orgmsLogo);
        const pdf = new jsPDF('p', 'mm', 'a4');
        const margin = 10;
        const PAGE_WIDTH = pdf.internal.pageSize.getWidth();
        const PAGE_HEIGHT = pdf.internal.pageSize.getHeight();
        const rowsPerPage = Math.floor((PAGE_HEIGHT - 50) / 10); // Adjust space for org info and title

        // Summary right values
        const summaryRightValues = [
            'Donation Report',
            `${getReportTitle()}`,
            `Report Date: ${new Intl.DateTimeFormat('en-US').format(new Date())}`
        ];

        // Fetch donation data to be displayed in the table
        const data = donations.map(donation => [
            donation.receiptNum,
            new Date(donation.receiptDate + 'T00:00:00').toLocaleDateString(),
            donation.member.isNameConfidential ? 'Anonymous' : `${donation.first1} ${donation.last1}`,
            donation.city,
            new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(donation.donationAmt))
        ]);

        const totalPages = Math.ceil(data.length / rowsPerPage);

        for (let page = 0; page < totalPages; page++) {
            if (page > 0) pdf.addPage();

            // Add org details to the left
            pdf.setFontSize(10);
            pdf.addImage(logoBase64, 'JPEG', (PAGE_WIDTH / 2) - 20, 10, 40, 20); // Center the logo in the middle

            // Left: Organization details
            pdf.text(orgDetails.name, margin, 15);
            pdf.text(`${orgDetails.address} ${orgDetails.address2}`, margin, 22);
            pdf.text(`${orgDetails.city}, ${orgDetails.state} ${orgDetails.zip}`, margin, 29);
            pdf.text(`Phone: ${orgDetails.phone}`, margin, 36);

            // Right: Summary right values
            const rightX = PAGE_WIDTH - margin - 60; // Adjust this value to the width of the right section
            pdf.setFontSize(10);
            summaryRightValues.forEach((value, index) => {
                pdf.text(value, rightX, 15 + (index * 7)); // Adjust the y position dynamically
            });

            // Add report title below the org details and logo
            //const title = getReportTitle();
            //pdf.setFontSize(16);
            //pdf.text(title, margin, 50); // Align title below the org details

            // Donation data table
            const pageData = data.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
            pdf.autoTable({
                startY: 60, // Adjust startY to leave space for the header
                head: [['Receipt Num', 'Date', 'Member Name', 'City', 'Amount']],
                body: pageData,
                margin: { top: 60, left: margin, right: margin },
                styles: { fontSize: 10 },
                columnStyles: { 4: { halign: 'right' } },
                tableWidth: 'wrap',
            });

            // Add total row only on the last page
            if (page === totalPages - 1) {
                const lastY = pdf.autoTable.previous.finalY;
                const totalY = lastY + 10;
                pdf.setFontSize(10);
                pdf.text('Total Donations:', margin, totalY); // Align label under "Receipt Num"
                pdf.text(
                    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalAmount),
                    PAGE_WIDTH - margin - 60,
                    totalY,
                    { align: 'right' }
                );
            }
            // Add page number
            pdf.setFontSize(10);
            pdf.text(`Page ${page + 1} of ${totalPages}`, PAGE_WIDTH - margin - 20, PAGE_HEIGHT - 10, { align: 'right' });
        }

        const fileName = month
            ? `DonationReport_${year}_${month}.pdf`
            : quarter
                ? `DonationReport_${year}_Q${quarter}.pdf`
                : `DonationReport_${year}.pdf`;

        pdf.save(fileName);
    };

    const years = Array.from({ length: new Date().getFullYear() - 1991 }, (_, i) => 1992 + i);


    // CSV Export Functionality
    const handleExportCSV = () => {
        if (donations.length === 0) return;

        const csvRows = [];
        // Header
        const headers = [
            'Receipt Num',
            'Date',
            'Member Name',
            'City',
            'Amount'
        ];
        csvRows.push(headers.join(','));

        // Data rows
        donations.forEach(donation => {
            csvRows.push([
                donation.receiptNum,
                new Date(donation.receiptDate + 'T00:00:00').toLocaleDateString(),
                donation.member?.isNameConfidential ? 'Anonymous' : `${donation.first1 || ''} ${donation.last1 || ''}`,
                donation.city,
                `$${parseFloat(donation.donationAmt).toFixed(2)}`
            ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(','));
        });

        // Add total row if there are donations
        if (donations.length > 0) {
            csvRows.push([
                '', '', '', 'Total Donations:',
                `$${totalAmount.toFixed(2)}`
            ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(','));
        }

        const csvData = new Blob([csvRows.join('\n')], { type: 'text/csv' });
        const csvURL = window.URL.createObjectURL(csvData);

        const tempLink = document.createElement('a');
        const fileName = month
            ? `DonationReport_${year}_${month}.csv`
            : quarter
                ? `DonationReport_${year}_Q${quarter}.csv`
                : `DonationReport_${year}.csv`;
        tempLink.href = csvURL;
        tempLink.setAttribute('download', fileName);
        tempLink.click();
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div className="alert alert-danger" role="alert"> Error: {error.message} </div>;
    return (
        <div>
            {role === "admin" && (
                <div className="d-flex justify-content-end button-group no-print">
                    <Button className="no-print small-font" onClick={handlePrintAll}>
                        Print
                    </Button>
                    <Button className="no-print small-font" onClick={handleDownloadPDF}>
                        Download PDF
                    </Button>
                    <Button className="no-print small-font" onClick={handleExportCSV}>
                        Export as CSV
                    </Button>
                </div>
            )}
            <div className="mb-4 no-print">
                <label className="form-label small-font" htmlFor="year-select">Year</label>
                <select
                    id="year-select"
                    className="form-select small-font"
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                >
                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>

                <label className="form-label mt-2 small-font" htmlFor="quarter-select">Quarter (Optional)</label>
                <select
                    id="quarter-select"
                    className="form-select small-font"
                    value={quarter}
                    onChange={(e) => {
                        setQuarter(e.target.value);
                        setMonth(''); // Reset month when a quarter is selected
                    }}
                >
                    <option value="">Select Quarter</option>
                    {quarters.map(q => <option key={q.value} value={q.value}>{q.label}</option>)}
                </select>

                <label className="form-label mt-2 small-font" htmlFor="month-select">Month (Optional)</label>
                <select
                    id="month-select"
                    className="form-select small-font"
                    value={month}
                    onChange={(e) => {
                        setMonth(e.target.value);
                        setQuarter(''); // Reset quarter when a month is selected
                    }}
                >
                    <option value="">Full Year</option>
                    {months.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                </select>
                <label className="form-label small-font font-weight-bold">Sort by Receipt Date</label>
                <div className="radio-box p-2">
                    <Form.Check
                        type="radio"
                        id="sort-desc"
                        name="sortOrder"
                        value="DESC"
                        label="Descending"
                        checked={sortOrder === 'DESC'}
                        onChange={(e) => setSortOrder(e.target.value)}
                        className="small-font"
                        inline
                    />
                    <Form.Check
                        type="radio"
                        id="sort-asc"
                        name="sortOrder"
                        value="ASC"
                        label="Ascending"
                        checked={sortOrder === 'ASC'}
                        onChange={(e) => setSortOrder(e.target.value)}
                        className="small-font"
                        inline
                    />
                </div>
            </div>
            <div className="container mt-4">
                <h2 className="mb-4 no-print">{getReportTitle()}</h2>
                {donations.length > 0 && (
                    <ReportHeader summaryRight={summaryRightValues} />
                )}
                <Table striped bordered hover size="sm" className="small-font">
                    <thead>
                        <tr>
                            <th>Receipt Num</th>
                            <th>Date</th>
                            <th>Member Name</th>
                            <th>City</th>
                            <th>Description</th>
                            <th className="text-end">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {donations.length > 0 ? (
                            donations.map(donation => (
                                <tr key={donation.receiptNum}>
                                    <td>{donation.receiptNum}</td>
                                    <td>{new Date(donation.receiptDate + 'T00:00:00').toLocaleDateString()}</td>
                                    <td>
                                        {
                                            donation?.member?.isNameConfidential
                                                ? 'Anonymous'
                                                : `${donation?.first1 || ''} ${donation?.last1 || ''}`
                                        }
                                    </td>

                                    <td>{donation.city}</td>
                                    <td>{donation.description}</td>
                                    <td className="text-end">{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(donation.donationAmt)}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="text-center small-font">No donations found for the selected period</td>
                            </tr>
                        )}
                        {donations.length > 0 && (
                            <tr>
                                <td colSpan="4" className="text-end"><strong>Total Donations:</strong></td>
                                <td className="text-end"><strong>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalAmount)}</strong></td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>
        </div>
    );
};

export default DonationList;